/**
 * NPC memory of the player (ความจำของชาวบ้าน): every townsperson and key NPC keeps a small score of
 * what the player has done for or against them, and every village (the zone) keeps one for the
 * whole place. Helping raises it (a quest handed in, a monster killed beside them, a gift), harming
 * lowers it (striking another player in front of them, killing one in their village); a name feared
 * for killing (karma, PK colour) weighs on it live, without being stored.
 *
 * The scores are plain integers in `PlayerProgress.npcMemory` (saved, sent and replayed as the rest
 * of the progress), changed only in `World.step`/`World.apply`, with no random draws: the same events
 * give the same memory on every machine.
 *
 * What the standing changes: the greeting and the talk (data/npcTalk.ts `standing`), the gesture
 * (a bow for a friend, a flinch from the hated), and what the merchant asks (`shopPrice`; selling
 * is not changed).
 */
import { FOLK } from '../data/folk';
import { AFFINITY, affinityTier } from '../data/npcAffinity';
import { fairPriceAt } from '../data/worldEvents';
import { PRICE_HINT, type Standing } from '../data/npcTalk';
import { pkColor, type Karma, type PkColor } from '../karma';
import { infamyPriceMult } from '../offlineWork';
import type { Entity, World, WorldEvent } from '../world';
import { actRoleOf } from './npcActs';
import type { CommandHandlers, SystemHost, WorldSystem } from './types';

/** What the player has done, as people remember it: per person (`memoryKey`) and per village (zone id). */
export interface NpcMemoryState {
  npc: Record<string, number>;
  town: Record<string, number>;
  /** Affinity (ความสนิทสนม, data/npcAffinity.ts), per person: unbounded by the memory's ±100, never decays. */
  aff: Record<string, number>;
  /** The day (progress.arena.day) each person last took an item gift. */
  giftDay: Record<string, string>;
}

/** Give a townsperson some silver (ตำลึง): a little goodwill, up to a point; or a good from the bag (systems/npcLife.ts). */
export type NpcMemoryCommand =
  | { t: 'npcGift'; id: number; gold: number }
  | { t: 'npcGiftItem'; id: number; good: string };

/** A gift was given to someone (systems/npcTalk.ts has them say thanks); someone's affinity reached a new tier. */
export type NpcMemoryEvent =
  | {
      type: 'npcGift';
      id: number;
      gold: number;
      /** The person's score after the gift. */
      score: number;
    }
  | { type: 'affinity'; key: string; name: string; tier: number };

/** The rules, in score points (a score runs from `min` to `max`). */
export const MEMORY = {
  min: -100,
  max: 100,
  /** A quest handed in: the elder who gave it, and the village. */
  questElder: 10,
  questTown: 6,
  /** A monster the player killed this close to someone (tiles) counts as saving them. */
  savedRange: 9,
  saved: 3,
  savedTown: 1,
  /** The player striking another player this close to someone (tiles), at most once per `strikeEvery` s. */
  strike: -2,
  strikeTown: -1,
  strikeEvery: 4,
  /** The player killing another player: everyone within hearing, and the village. */
  pkKill: -12,
  pkKillTown: -10,
  /** Gifts: the least silver taken, silver per point, at most this many points a gift, never past `giftCap`. */
  giftMin: 10,
  giftMax: 10000,
  giftPer: 20,
  giftMostPoints: 5,
  giftCap: 35,
  giftEvery: 30,
  /** How much of the village's score each person shares. */
  townShare: 0.5,
  /** Live weight of a feared name. */
  karma: { good: 5, normal: 0, evil: -20, mad: -40 } satisfies Record<Karma, number>,
  pk: { none: 0, orange: -5, red: -30, purple: -50 } satisfies Record<PkColor, number>,
  /** Tier thresholds: at or below `hated`/`wary`, at or above `friendly`/`honoured`. */
  hated: -45,
  wary: -15,
  friendly: 15,
  honoured: 45,
} as const;

/** What a tier pays at the shop. */
export const PRICE_MULT: Readonly<Record<Standing, number>> = {
  hated: 1.25,
  wary: 1.1,
  neutral: 1,
  friendly: 0.95,
  honoured: 0.9,
};

export const newNpcMemory = (): NpcMemoryState => ({ npc: {}, town: {}, aff: {}, giftDay: {} });

/**
 * The progress's memory, made empty first when a progress has none (one built by hand, not through
 * `newProgress`/`parseProgress`: the shots' fixture, a replica's partial progress).
 */
export function memoryOf(pr: { npcMemory?: NpcMemoryState }): NpcMemoryState {
  const cur = pr.npcMemory;
  if (cur && typeof cur === 'object' && cur.npc && cur.town) {
    cur.aff ??= {};
    cur.giftDay ??= {};
    return cur;
  }
  return (pr.npcMemory = parseNpcMemory(cur));
}

/** A saved memory kept within its limits (saves from before it start with none). */
export function parseNpcMemory(raw: unknown): NpcMemoryState {
  const out = newNpcMemory();
  if (!raw || typeof raw !== 'object') return out;
  const r = raw as Record<string, unknown>;
  for (const k of ['npc', 'town'] as const) {
    const m = r[k];
    if (!m || typeof m !== 'object') continue;
    for (const [id, v] of Object.entries(m as Record<string, unknown>))
      if (typeof v === 'number' && Number.isFinite(v) && id.length <= 64) {
        const n = clamp(Math.round(v));
        if (n !== 0) out[k][id] = n;
      }
  }
  const aff = r.aff;
  if (aff && typeof aff === 'object')
    for (const [id, v] of Object.entries(aff as Record<string, unknown>))
      if (typeof v === 'number' && Number.isFinite(v) && id.length <= 64) {
        const n = Math.max(AFFINITY.min, Math.min(AFFINITY.max, Math.round(v)));
        if (n !== 0) out.aff[id] = n;
      }
  const gd = r.giftDay;
  if (gd && typeof gd === 'object')
    for (const [id, v] of Object.entries(gd as Record<string, unknown>))
      if (typeof v === 'string' && v.length <= 16 && id.length <= 64) out.giftDay[id] = v;
  return out;
}

const clamp = (v: number): number => Math.max(MEMORY.min, Math.min(MEMORY.max, v));

/** Adds to a score (rounded, clamped; a score back at 0 is forgotten). */
export function addScore(m: Record<string, number>, key: string, d: number): number {
  const v = clamp(Math.round((m[key] ?? 0) + d));
  if (v === 0) delete m[key];
  else m[key] = v;
  return v;
}

/** A person's affinity score (0 if none). */
export const affinityOf = (pr: { npcMemory?: NpcMemoryState }, key: string): number => memoryOf(pr).aff[key] ?? 0;

/**
 * Adds to a person's affinity; a new tier is announced (an `affinity` event, a notice). Returns the new score.
 * `name` is who it is (for the notice).
 */
export function addAffinity(w: World, emit: (e: WorldEvent) => void, key: string, d: number, name: string): number {
  const m = memoryOf(w.progress).aff;
  const was = m[key] ?? 0;
  const now = Math.max(AFFINITY.min, Math.min(AFFINITY.max, Math.round(was + d)));
  if (now === 0) delete m[key];
  else m[key] = now;
  const a = affinityTier(was).n;
  const b = affinityTier(now).n;
  if (b > a) emit({ type: 'affinity', key, name, tier: b });
  return now;
}

/** The affinity tier the player has with someone (null for anyone not remembered: monsters, other players). */
export function affinityTierOf(w: World, e: Entity): ReturnType<typeof affinityTier> | null {
  const key = memoryKey(w, e);
  return key ? affinityTier(affinityOf(w.progress, key)) : null;
}

/** Memory keys set by other systems for their own people (`setMemoryKey`). */
const OWN_KEYS = new WeakMap<Entity, string>();

/** Gives someone a memory key of their own (systems/npcLife.ts): they are remembered, priced and gifted by it. */
export const setMemoryKey = (e: Entity, key: string): void => void OWN_KEYS.set(e, key);

/**
 * Whom a memory belongs to: a townsperson by their folk id, a key NPC by zone and post (the elder of
 * one village is not the elder of the next), a school master by zone and school. Null for anyone
 * else (monsters, critters, other players).
 */
export function memoryKey(w: World, e: Entity): string | null {
  // Someone another system keys itself (systems/npcLife.ts: the plains' people, by zone and id).
  const own = OWN_KEYS.get(e);
  if (own) return own;
  const f = e.folk;
  // An act person (systems/npcActs.ts), by zone and name.
  if (!f) return actRoleOf(e) ? `${w.map.zone}/act/${e.name}` : null;
  if (!f.fixed) return f.id;
  const zone = w.map.zone;
  if (f.id === 'master') {
    for (const [school, m] of w.schoolMasters) if (m === e) return `${zone}/master/${school}`;
    return `${zone}/master`;
  }
  return `${zone}/${f.id}`;
}

/** How someone regards the player, as a score: their own memory, the village's share, and the name. */
export function standingScore(w: World, e: Entity): number {
  const key = memoryKey(w, e);
  if (!key) return 0;
  const pr = w.progress;
  const m = memoryOf(pr);
  return (
    (m.npc[key] ?? 0) +
    (m.town[w.map.zone] ?? 0) * MEMORY.townShare +
    MEMORY.karma[pr.karma] +
    MEMORY.pk[pkColor(pr.pkPoints)]
  );
}

export function standingTier(score: number): Standing {
  if (score <= MEMORY.hated) return 'hated';
  if (score <= MEMORY.wary) return 'wary';
  if (score >= MEMORY.honoured) return 'honoured';
  if (score >= MEMORY.friendly) return 'friendly';
  return 'neutral';
}

export const standingOf = (w: World, e: Entity): Standing => standingTier(standingScore(w, e));

/** The shop's price factor for this person. */
export const priceMultFor = (w: World, e: Entity): number => PRICE_MULT[standingOf(w, e)];

/** What the merchant asks of the player for an item listed at `base` (selling is not changed). */
export const shopPrice = (w: World, base: number): number =>
  // ชื่อฉาว (offlineWork.ts) adds its own mark-up once the name reads red.
  Math.max(1, Math.round(base * priceMultFor(w, w.merchant) * infamyPriceMult(w.progress) * fairPriceAt(w.map.zone, w.time)));

/** The shop's note on the price (data/npcTalk.ts `PRICE_HINT`), or null at the plain price. */
export const shopPriceHint = (w: World): string | null => {
  const hint = PRICE_HINT[standingOf(w, w.merchant)];
  // The fair of the new Yanyu (data/worldEvents.ts) takes a fifth off while it runs.
  const fair = fairPriceAt(w.map.zone, w.time) < 1 ? 'ตลาดนัด ราคาลด 20%' : null;
  return fair ? (hint ? `${fair} · ${hint}` : fair) : hint;
};

/** Townsfolk, key NPCs and the people other systems key (the plains' life people) out of doors within `r` tiles of (x, y). */
export function peopleNear(w: World, x: number, y: number, r: number): Entity[] {
  const out: Entity[] = [];
  const near = (e: Entity) => !e.dead && Math.hypot(e.x - x, e.y - y) <= r;
  for (const e of w.folk.values()) if (!e.folk!.indoors && near(e)) out.push(e);
  for (const e of w.entities.values()) if (OWN_KEYS.has(e) && near(e)) out.push(e);
  for (const e of [w.elder, w.merchant, w.guide, ...w.schoolMasters.values()])
    if (e.folk && near(e)) out.push(e);
  return out;
}

/** The player struck or killed another player outside a bout. */
const isPkTarget = (t: Entity | undefined): t is Entity => !!t && (t.kind === 'player' || t.kind === 'rival');

export class NpcMemorySystem implements WorldSystem {
  readonly name = 'npcMemory';
  /** When each person (and the village, as `town:`) last took a strike to heart, and last got a gift. */
  private readonly struck = new Map<string, number>();
  private readonly gifted = new Map<string, number>();

  constructor(private readonly h: SystemHost) {}

  readonly commands: CommandHandlers = {
    npcGift: (c) => this.gift(c.id, c.gold),
  };

  observe(ev: WorldEvent): void {
    const w = this.h.world;
    const p = w.player;
    const m = memoryOf(w.progress);
    switch (ev.type) {
      case 'quest':
        if (ev.status !== 'completed') return;
        addScore(m.npc, memoryKey(w, w.elder) ?? `${w.map.zone}/elder`, MEMORY.questElder);
        addAffinity(w, (x) => this.h.emit(x), memoryKey(w, w.elder) ?? `${w.map.zone}/elder`, AFFINITY.quest, w.elder.name);
        addScore(m.town, w.map.zone, MEMORY.questTown);
        return;
      case 'death': {
        const mon = w.get(ev.id);
        if (!mon || mon.kind !== 'monster' || p.dead) return;
        // Killed by the player: the one it was after, or within reach of the player's blade.
        if (mon.targetId !== p.id && Math.hypot(mon.x - p.x, mon.y - p.y) > 6) return;
        const saved = peopleNear(w, mon.x, mon.y, MEMORY.savedRange);
        for (const e of saved) addScore(m.npc, memoryKey(w, e)!, MEMORY.saved);
        if (saved.length) addScore(m.town, w.map.zone, MEMORY.savedTown);
        return;
      }
      case 'hit': {
        if (ev.srcId !== p.id || ev.damage <= 0) return;
        const t = w.get(ev.tgtId);
        if (!isPkTarget(t) || t === p || w.duel?.phase === 'fight') return;
        for (const e of peopleNear(w, t.x, t.y, FOLK.hearing)) {
          const key = memoryKey(w, e)!;
          if (this.strike(key, m.npc, MEMORY.strike)) addAffinity(w, (x) => this.h.emit(x), key, AFFINITY.strike, e.name);
        }
        this.strike(`town:${w.map.zone}`, m.town, MEMORY.strikeTown, w.map.zone);
        return;
      }
      case 'karma': {
        if (ev.id !== p.id) return;
        for (const e of peopleNear(w, p.x, p.y, FOLK.hearing)) {
          addScore(m.npc, memoryKey(w, e)!, MEMORY.pkKill);
          addAffinity(w, (x) => this.h.emit(x), memoryKey(w, e)!, AFFINITY.pkKill, e.name);
        }
        addScore(m.town, w.map.zone, MEMORY.pkKillTown);
        return;
      }
    }
  }

  /** A strike taken to heart, once per `strikeEvery` seconds per person. */
  private strike(tag: string, scores: Record<string, number>, d: number, key = tag): boolean {
    const now = this.h.world.time;
    const last = this.struck.get(tag);
    if (last !== undefined && now - last < MEMORY.strikeEvery) return false;
    this.struck.set(tag, now);
    addScore(scores, key, d);
    return true;
  }

  private gift(id: number, gold: number): void {
    const w = this.h.world;
    const pr = w.progress;
    const e = w.get(id);
    const key = e ? memoryKey(w, e) : null;
    if (!e || !key || w.player.dead) return;
    if (Math.hypot(e.x - w.player.x, e.y - w.player.y) > FOLK.notice + 1) return;
    if (!Number.isInteger(gold) || gold < MEMORY.giftMin || gold > MEMORY.giftMax) return;
    if (pr.gold < gold) return this.h.notice('ตำลึงไม่พอ');
    const last = this.gifted.get(key);
    if (last !== undefined && w.time - last < MEMORY.giftEvery) return this.h.notice('เพิ่งให้ไปเมื่อครู่');
    this.gifted.set(key, w.time);
    pr.gold -= gold;
    const mem = memoryOf(pr);
    const now = mem.npc[key] ?? 0;
    const gain = Math.min(MEMORY.giftMostPoints, Math.floor(gold / MEMORY.giftPer) + 1);
    const score = now < MEMORY.giftCap ? addScore(mem.npc, key, Math.min(gain, MEMORY.giftCap - now)) : now;
    if (score > now) addAffinity(w, (x) => this.h.emit(x), key, (score - now) * AFFINITY.goldPerMemory, e.name);
    this.h.log('sys', `มอบ ${gold.toLocaleString('th-TH')} ตำลึงให้${e.name}`);
    this.h.emit({ type: 'npcGift', id, gold, score });
    this.h.emit({ type: 'progressChanged' });
  }
}
